const STORAGE_PREFIX = 'overview_stats_graph_users_';
const HIDDEN_STORAGE_PREFIX = 'overview_stats_graph_hidden_users_';

function storageKey(projectId) {
  return `${STORAGE_PREFIX}${projectId}`;
}

function hiddenStorageKey(projectId) {
  return `${HIDDEN_STORAGE_PREFIX}${projectId}`;
}

function parsePositiveIntIds(raw) {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const seen = new Set();
    const ids = [];
    for (const value of parsed) {
      const id = Number(value);
      if (!Number.isInteger(id) || id <= 0 || seen.has(id)) {
        continue;
      }
      seen.add(id);
      ids.push(id);
    }
    return ids;
  } catch (e) {
    return [];
  }
}

function normalizePositiveIntIds(userIds) {
  const seen = new Set();
  const ids = [];
  for (const value of userIds || []) {
    const id = Number(value);
    if (!Number.isInteger(id) || id <= 0 || seen.has(id)) {
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * @param {number|string|null|undefined} projectId
 * @returns {number[]}
 */
export function getOverviewGraphUserIds(projectId) {
  if (projectId == null || projectId === '' || projectId === false) {
    return [];
  }
  return parsePositiveIntIds(localStorage.getItem(storageKey(projectId)));
}

/**
 * @param {number|string|null|undefined} projectId
 * @param {Array<number|string>} userIds
 * @returns {number[]}
 */
export function setOverviewGraphUserIds(projectId, userIds) {
  if (projectId == null || projectId === '' || projectId === false) {
    return [];
  }
  const ids = normalizePositiveIntIds(userIds);
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(ids));
  } catch (e) {
    /* quota or private mode */
  }
  return ids;
}

/**
 * @param {number|string|null|undefined} projectId
 * @param {number|string} userId
 * @returns {number[]}
 */
export function addOverviewGraphUserId(projectId, userId) {
  const ids = getOverviewGraphUserIds(projectId);
  const id = Number(userId);
  if (!Number.isInteger(id) || id <= 0 || ids.includes(id)) {
    return ids;
  }
  ids.push(id);
  return setOverviewGraphUserIds(projectId, ids);
}

/**
 * @param {number|string|null|undefined} projectId
 * @param {number|string} userId
 * @returns {number[]}
 */
export function removeOverviewGraphUserId(projectId, userId) {
  const id = Number(userId);
  const ids = getOverviewGraphUserIds(projectId).filter((existing) => existing !== id);
  removeOverviewGraphHiddenUserId(projectId, id);
  return setOverviewGraphUserIds(projectId, ids);
}

/**
 * @param {number|string|null|undefined} projectId
 * @returns {number[]}
 */
export function getOverviewGraphHiddenUserIds(projectId) {
  if (projectId == null || projectId === '' || projectId === false) {
    return [];
  }
  return parsePositiveIntIds(localStorage.getItem(hiddenStorageKey(projectId)));
}

/**
 * @param {number|string|null|undefined} projectId
 * @param {Array<number|string>} userIds
 * @returns {number[]}
 */
export function setOverviewGraphHiddenUserIds(projectId, userIds) {
  if (projectId == null || projectId === '' || projectId === false) {
    return [];
  }
  const ids = normalizePositiveIntIds(userIds);
  try {
    localStorage.setItem(hiddenStorageKey(projectId), JSON.stringify(ids));
  } catch (e) {
    /* quota or private mode */
  }
  return ids;
}

/**
 * @param {number|string|null|undefined} projectId
 * @param {number|string} userId
 * @returns {number[]}
 */
export function toggleOverviewGraphUserVisibility(projectId, userId) {
  const id = Number(userId);
  const hidden = getOverviewGraphHiddenUserIds(projectId);
  const next = hidden.includes(id)
    ? hidden.filter((existing) => existing !== id)
    : [...hidden, id];
  return setOverviewGraphHiddenUserIds(projectId, next);
}

/**
 * @param {number|string|null|undefined} projectId
 * @param {number|string} userId
 * @returns {number[]}
 */
export function removeOverviewGraphHiddenUserId(projectId, userId) {
  const id = Number(userId);
  const hidden = getOverviewGraphHiddenUserIds(projectId).filter((existing) => existing !== id);
  return setOverviewGraphHiddenUserIds(projectId, hidden);
}

export function overviewGraphUserStorageKey(projectId) {
  return storageKey(projectId);
}

export function overviewGraphHiddenUserStorageKey(projectId) {
  return hiddenStorageKey(projectId);
}

const SERIES_HIDDEN_STORAGE_PREFIX = 'overview_stats_graph_hidden_series_';
export const OVERVIEW_GRAPH_PROJECT_SERIES_KEYS = [
  'items_added',
  'assets_edited',
  'annotations_validated',
];

function seriesHiddenStorageKey(projectId) {
  return `${SERIES_HIDDEN_STORAGE_PREFIX}${projectId}`;
}

function parseSeriesKeys(raw) {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const allowed = new Set(OVERVIEW_GRAPH_PROJECT_SERIES_KEYS);
    const seen = new Set();
    const keys = [];
    for (const value of parsed) {
      if (typeof value !== 'string' || !allowed.has(value) || seen.has(value)) {
        continue;
      }
      seen.add(value);
      keys.push(value);
    }
    return keys;
  } catch (e) {
    return [];
  }
}

function normalizeSeriesKeys(seriesKeys) {
  const allowed = new Set(OVERVIEW_GRAPH_PROJECT_SERIES_KEYS);
  const seen = new Set();
  const keys = [];
  for (const value of seriesKeys || []) {
    if (typeof value !== 'string' || !allowed.has(value) || seen.has(value)) {
      continue;
    }
    seen.add(value);
    keys.push(value);
  }
  return keys;
}

/**
 * @param {number|string|null|undefined} projectId
 * @returns {string[]}
 */
export function getOverviewGraphHiddenSeriesKeys(projectId) {
  if (projectId == null || projectId === '' || projectId === false) {
    return [];
  }
  return parseSeriesKeys(localStorage.getItem(seriesHiddenStorageKey(projectId)));
}

/**
 * @param {number|string|null|undefined} projectId
 * @param {Array<string>} seriesKeys
 * @returns {string[]}
 */
export function setOverviewGraphHiddenSeriesKeys(projectId, seriesKeys) {
  if (projectId == null || projectId === '' || projectId === false) {
    return [];
  }
  const keys = normalizeSeriesKeys(seriesKeys);
  try {
    localStorage.setItem(seriesHiddenStorageKey(projectId), JSON.stringify(keys));
  } catch (e) {
    /* quota or private mode */
  }
  return keys;
}

/**
 * @param {number|string|null|undefined} projectId
 * @param {string} seriesKey
 * @returns {string[]}
 */
export function toggleOverviewGraphSeriesVisibility(projectId, seriesKey) {
  const hidden = getOverviewGraphHiddenSeriesKeys(projectId);
  const next = hidden.includes(seriesKey)
    ? hidden.filter((existing) => existing !== seriesKey)
    : [...hidden, seriesKey];
  return setOverviewGraphHiddenSeriesKeys(projectId, next);
}

export function overviewGraphHiddenSeriesStorageKey(projectId) {
  return seriesHiddenStorageKey(projectId);
}
